import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  type FormProps,
  Input,
  Radio,
  Row,
  Select,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useMatch } from 'react-router-dom';

import { FormItem } from '@/components';
import { DATE_FORMAT_JP, TIME_ZONE } from '@/constants/datetime.constants';
import {
  FORM_ITEM_LAYOUT_COMMON,
  FORM_ITEM_LAYOUT_WITH_CHECKBOX,
} from '@/constants/form.constants';
import {
  CONTACT_NAME_PATTERN,
  EMAIL_PATTERN,
} from '@/constants/regex.constants';
import { ROUTES_PATH } from '@/constants/routes.constants';
import { SHOP_CONTRACT_TYPES_OPTIONS } from '@/features/shop/constants';
import { transformEmojisIfHave } from '@/helpers/string.helper';
import { useDetectFormValuesChanged } from '@/hooks/use-detect-form-values-changed';
import { useFormValidation } from '@/hooks/use-form-validation';
import { useModalMethod } from '@/hooks/use-modal-method';
import { useUserInformation } from '@/hooks/use-user-information';

import { useSaleAreas } from '../../queries/sale-areas';
import type { OrganizationRequest } from '../../types';

const { Option } = Select;
const { Text } = Typography;

interface OrganizationFormProps extends FormProps {
  isUpdating?: boolean;
  isPreviewMode?: boolean;
}

export function OrganizationForm({
  isUpdating,
  isPreviewMode,
  initialValues,
  form: externalForm,
  ...rest
}: Readonly<OrganizationFormProps>) {
  const { isSbsRoles } = useUserInformation();
  const { confirm } = useModalMethod();
  const [internalForm] = Form.useForm();
  const form = externalForm ?? internalForm;
  const { sale_areas: saleAreas } = useSaleAreas();
  const isMyOrganization = !!useMatch(ROUTES_PATH.myOrganization);
  const { values, submittable } = useFormValidation<OrganizationRequest>({
    form,
  });

  const operationStartDate = values?.operation_start_date;
  const operationEndDate = values?.operation_end_date;
  const contractType = values?.contract_type;

  const {
    name,
    pic_name,
    pic_email,
    sale_area_ids,
    operation_start_date,
    operation_end_date,
    is_use_wedding_store,
    contract_type,
    is_contract_type_editable: isContractTypeEditable,
  } = initialValues ?? {};
  const parsedInitialValues = {
    name,
    ...(!isMyOrganization && {
      sale_area_ids,
      is_use_wedding_store: is_use_wedding_store ?? false,
      ...(isUpdating && { contract_type }),
    }),
    ...(isUpdating
      ? {
          operation_end_date:
            operation_end_date && dayjs(operation_end_date).tz(TIME_ZONE),
          operation_start_date:
            operation_start_date && dayjs(operation_start_date).tz(TIME_ZONE),
        }
      : { pic_name, pic_email }),
  };

  const { handleFieldsChange } = useDetectFormValuesChanged({
    form,
    isOrganizationUpdate: true,
    initialValues: parsedInitialValues,
  });

  async function handleSubmit() {
    if (
      isUpdating &&
      isContractTypeEditable &&
      contract_type !== contractType &&
      !is_use_wedding_store
    ) {
      const confirmed = await confirm({
        title: '契約種類の変更',
        okText: '変更',
        content:
          '契約種類を変更すると過去の販売実績のデータが削除されます。よろしいですか？',
        onOk: () => form.submit(),
      });
      if (!confirmed) return;
      return;
    }
    form.submit();
  }

  return (
    <Form
      form={form}
      labelAlign="left"
      initialValues={parsedInitialValues}
      onFieldsChange={handleFieldsChange}
      {...rest}
    >
      <Card variant="borderless">
        {isUpdating && isSbsRoles && !is_use_wedding_store && (
          <FormItem
            label="契約種類"
            name="contract_type"
            validateFirst
            rules={[{ required: !!contract_type }]}
            {...FORM_ITEM_LAYOUT_COMMON}
          >
            <Select
              optionLabelProp="label"
              className="select-option-has-description"
              disabled={!isContractTypeEditable || !contract_type}
            >
              {contract_type ? (
                SHOP_CONTRACT_TYPES_OPTIONS.map(
                  ({ label, value, description }) => (
                    <Option key={value} value={value} label={label}>
                      <Text className="text-md">{label}</Text>
                      <Text className="text-sm d-block" type="secondary">
                        {description}
                      </Text>
                    </Option>
                  ),
                )
              ) : (
                <Option value={undefined} label="-">
                  -
                </Option>
              )}
            </Select>
          </FormItem>
        )}
        <FormItem
          label="チェーン名"
          name="name"
          validateFirst
          rules={[
            { required: true, whitespace: true },
            {
              max: 512,
              transform(value) {
                return transformEmojisIfHave(value);
              },
            },
          ]}
          textPreview={values?.name}
          isPreviewMode={isPreviewMode}
          {...FORM_ITEM_LAYOUT_COMMON}
        >
          <Input disabled={isMyOrganization} />
        </FormItem>
        {!isUpdating && (
          <>
            <FormItem
              label="担当者名"
              name="pic_name"
              validateFirst
              textPreview={values?.pic_name}
              isPreviewMode={isPreviewMode}
              rules={[
                { required: true, whitespace: true },
                {
                  max: 512,
                  transform(value) {
                    return transformEmojisIfHave(value);
                  },
                },
                {
                  pattern: CONTACT_NAME_PATTERN,
                  message:
                    '全角カタカナ、ひらがな、漢字、アルファベットのみで入力してください。',
                },
              ]}
              {...FORM_ITEM_LAYOUT_COMMON}
            >
              <Input placeholder="フルネームを入力してください" />
            </FormItem>
            <FormItem
              label="担当者メールアドレス"
              name="pic_email"
              validateFirst
              textPreview={values?.pic_email}
              isPreviewMode={isPreviewMode}
              rules={[
                { required: true, whitespace: true },
                {
                  max: 128,
                  transform(value) {
                    return transformEmojisIfHave(value);
                  },
                },
                {
                  pattern: EMAIL_PATTERN,
                  message: 'メールアドレスの形式で入力してください。',
                },
              ]}
              {...FORM_ITEM_LAYOUT_COMMON}
            >
              <Input />
            </FormItem>
          </>
        )}
        {!isMyOrganization && (
          <FormItem
            label="企画モデルの利用"
            name="is_use_wedding_store"
            rules={[{ required: true }]}
            textPreview={values?.is_use_wedding_store ? 'あり' : 'なし'}
            isPreviewMode={isPreviewMode}
            {...FORM_ITEM_LAYOUT_COMMON}
          >
            <Radio.Group>
              <Radio value={false}>なし</Radio>
              <Radio value={true}>あり</Radio>
            </Radio.Group>
          </FormItem>
        )}
        {isUpdating && (
          <>
            <FormItem
              label="契約開始日"
              name="operation_start_date"
              required={false}
              dependencies={['operation_end_date']}
              rules={[{ required: !!operationEndDate }]}
              textPreview={operationStartDate?.format(DATE_FORMAT_JP) ?? '-'}
              isPreviewMode={isPreviewMode}
              {...FORM_ITEM_LAYOUT_COMMON}
            >
              <DatePicker
                inputReadOnly
                format={DATE_FORMAT_JP}
                disabled={isMyOrganization}
                placeholder="日付を選択してください"
                disabledDate={date =>
                  operationEndDate &&
                  date.startOf('day').isAfter(operationEndDate.startOf('day'))
                }
              />
            </FormItem>
            <FormItem
              label="契約終了日"
              name="operation_end_date"
              textPreview={operationEndDate?.format(DATE_FORMAT_JP) ?? '-'}
              isPreviewMode={isPreviewMode}
              {...FORM_ITEM_LAYOUT_COMMON}
            >
              <DatePicker
                inputReadOnly
                format={DATE_FORMAT_JP}
                disabled={isMyOrganization}
                placeholder="日付を選択してください"
                disabledDate={date =>
                  operationStartDate &&
                  date
                    .startOf('day')
                    .isBefore(operationStartDate.startOf('day'))
                }
              />
            </FormItem>
          </>
        )}
        {!isMyOrganization && (
          <FormItem
            isLastFormItem
            label="担当支社"
            name="sale_area_ids"
            rules={[{ required: true }]}
            textPreview={
              saleAreas
                ?.filter(({ id }) => values?.sale_area_ids?.includes(id))
                ?.map(({ name }) => name)
                ?.join('、') ?? '-'
            }
            isPreviewMode={isPreviewMode}
            {...FORM_ITEM_LAYOUT_WITH_CHECKBOX}
          >
            <Checkbox.Group>
              <Row gutter={[16, 8]}>
                {saleAreas?.map(({ id, name }) => (
                  <Col span={8} key={id}>
                    <Checkbox value={id}>{name}</Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </FormItem>
        )}
      </Card>
      {!isMyOrganization && (
        <Flex className="mt-32" justify="center">
          <Button
            block
            type="primary"
            disabled={!submittable}
            onClick={handleSubmit}
          >
            {isUpdating ? '更新' : '登録'}
          </Button>
        </Flex>
      )}
    </Form>
  );
}
